import type { RevenueAnalysisForecastSeries } from '@/shared/api/modules/facilities';
import { chartColor } from '@/shared/lib/chart-theme';
import { getDynamicChartScale } from '@/shared/lib/charts';
import { formatCurrency } from '@/shared/lib/currency';
import { formatNumberCompact } from '@/shared/lib/formatNumberCompact';
import { MultiLineChart } from '@/shared/ui/charts';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import dayjs from 'dayjs';
import type { FC } from 'react';

type ForecastChartProps = {
  series: RevenueAnalysisForecastSeries;
};

/**
 * The Forecasted tab: actuals in solid primary, the three projected months in a
 * lighter tint.
 *
 * Deliberately a copy of the General Dashboard's Forecasted Revenue chart rather
 * than an import of it — that card owns its own query (a fixed, unpageable
 * window) and #137 requires its behaviour stay untouched. What is shared is the
 * shape: the forecast dataset restates the last actual point so the two lines
 * join, and the tooltip hides that duplicated point.
 */
export const ForecastChart: FC<ForecastChartProps> = ({ series }) => {
  const { actuals, forecast } = series;

  const data: ChartData<'line'> = {
    labels: [...actuals, ...forecast].map((p) =>
      dayjs(`${p.month}-01`).format('MMM YYYY')
    ),
    datasets: [
      {
        label: 'Actual',
        data: actuals.map((p) => p.value),
        borderColor: chartColor('--color-primary-7'),
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointRadius: 4,
      },
      {
        label: 'Forecast',
        data: [
          ...actuals.slice(0, -1).map(() => null),
          ...(actuals.length > 0 ? [actuals[actuals.length - 1].value] : []),
          ...forecast.map((p) => p.value),
        ],
        borderColor: chartColor('--color-primary-3'),
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointRadius: 4,
      },
    ],
  };

  const { min, max, stepSize } = getDynamicChartScale([
    ...actuals.map((p) => p.value),
    ...forecast.map((p) => p.value),
  ]);

  const options: ChartOptions<'line'> = {
    scales: {
      x: { offset: true, grid: { drawTicks: false, offset: true } },
      y: {
        min,
        suggestedMax: max,
        ticks: { stepSize, callback: (value) => formatNumberCompact(+value) },
        grid: { drawTicks: false },
      },
    },
    plugins: {
      legend: { display: false },
      // This object replaces MultiLineChart's whole `plugins` block, so its
      // datalabels default has to be restated or every point prints its value.
      datalabels: { display: false },
      tooltip: {
        enabled: true,
        // The joining point belongs to both datasets; showing it twice would
        // read as two different values for the same month.
        filter: (item: TooltipItem<'line'>) =>
          !(item.dataIndex === actuals.length - 1 && item.datasetIndex === 1),
        callbacks: {
          label: (item: TooltipItem<'line'>) =>
            `${item.dataset.label}: ${formatCurrency(item.parsed.y ?? 0)}`,
        },
      },
    },
  };

  return <MultiLineChart data={data} optionsProps={options} />;
};
